import { TBlockchain } from '@ankr.com/advanced-api/src/api/getLogs/types';

import { IJwtApiKey } from '../../accounting';
import {
  IJwtToken,
  JwtTokenFullData,
  Web3Address,
  WorkerTokenData,
} from '../../common';
import {
  METAMASK_REJECTED_OPERATION_CODE,
  TokenDecryptionService,
} from '../tokenDecryptionService';
import {
  BaseTokenIssuerService,
  BaseTokenIssuerServiceParams,
} from './BaseTokenIssuerService';
import { isExpiredTokenError } from './TokenIssuerServiceUtils';

interface Web3TokenIssuerServiceParams extends BaseTokenIssuerServiceParams {
  tokenDecryptionService: TokenDecryptionService;
}

// Converts a decrypted JWT into an API key. The worker seals the endpoint
// token once API keys are sealed, so the conversion has to go through the
// accounting gateway, which only the caller holds with the user's auth token.
export type ConvertJwtToApiKey = (jwtToken: string) => Promise<IJwtApiKey>;

export class Web3TokenIssuerService extends BaseTokenIssuerService {
  private tokenDecryptionService: TokenDecryptionService;

  public constructor(params: Web3TokenIssuerServiceParams) {
    const { tokenDecryptionService, ...rest } = params;

    super(rest);

    this.tokenDecryptionService = tokenDecryptionService;
  }

  private async upgradeJwtToken(
    jwtToken: IJwtToken,
    convertJwtToApiKey: ConvertJwtToApiKey,
  ): Promise<JwtTokenFullData> {
    const { signed_token: signedToken, expires_at: expiresAt } = jwtToken;
    const decryptedToken = await this.tokenDecryptionService.decryptToken(
      signedToken,
      expiresAt,
    );

    const { token, tier, enterpriseApiKeys, partnerChains } =
      await convertJwtToApiKey(decryptedToken);

    const workerTokenData: WorkerTokenData = {
      signedToken: decryptedToken,
      userEndpointToken: token,
      tier,
      enterpriseApiKeys,
      partnerChains,
    };

    return { jwtToken, workerTokenData };
  }

  public async getAllLatestUserTierAssignedEventLogHashes(
    user: Web3Address,
    blockchain: TBlockchain,
  ) {
    return this.PAYGContractManager.getAllLatestUserTierAssignedEventLogHashes(
      user,
      blockchain,
    );
  }

  public async getAllIssuedJwtTokens(user: Web3Address) {
    return this.getTokenFinderService().getAllIssuedJwtTokens(user);
  }

  private async findIssuedTokenAndUpgrade(
    user: Web3Address,
    convertJwtToApiKey: ConvertJwtToApiKey,
  ) {
    const firstActiveToken = await this.findIssuedToken(user);

    if (!firstActiveToken) {
      return undefined;
    }

    try {
      const fullTokenData = await this.upgradeJwtToken(
        firstActiveToken,
        convertJwtToApiKey,
      );

      return fullTokenData;
    } catch (error) {
      if (isExpiredTokenError(error)) {
        return { jwtToken: firstActiveToken };
      }

      throw error;
    }
  }

  public async getIssuedJwtTokenOrIssue(
    user: Web3Address,
    blockchain: TBlockchain,
    convertJwtToApiKey: ConvertJwtToApiKey,
  ): Promise<JwtTokenFullData> {
    const issuedToken = await this.findIssuedTokenAndUpgrade(
      user,
      convertJwtToApiKey,
    );

    if (issuedToken) {
      return issuedToken;
    }

    return this.issueJwtToken(user, blockchain, convertJwtToApiKey);
  }

  public async issueJwtToken(
    user: Web3Address,
    blockchain: TBlockchain,
    convertJwtToApiKey: ConvertJwtToApiKey,
  ) {
    const PAYGTransactionHash = await this.getPAYGTransactionHash(
      user,
      blockchain,
    );

    if (!PAYGTransactionHash) return {};

    const thresholdKeyId = await this.getThresholdKey();

    if (!thresholdKeyId) return {};

    // try to requestJwtToken with sigUtil publickey
    try {
      if (!this.tokenDecryptionService.publicKey) {
        await this.tokenDecryptionService.requestEncryptionKeys();
      }

      const jwtToken = await this.getConsensusGateway().requestJwtToken({
        public_key: this.tokenDecryptionService.publicKey,
        threshold_key: thresholdKeyId,
        transaction_hash: PAYGTransactionHash,
      });

      const tokenFullData = await this.upgradeJwtToken(
        jwtToken,
        convertJwtToApiKey,
      );

      return tokenFullData;
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        error?.code === METAMASK_REJECTED_OPERATION_CODE
      ) {
        throw error;
      }

      // if error we try to use mm public key
      const publicKey =
        await this.tokenDecryptionService.requestMetamaskEncryptionKey();

      const jwtToken = await this.getConsensusGateway().requestJwtToken({
        public_key: publicKey,
        threshold_key: thresholdKeyId,
        transaction_hash: PAYGTransactionHash,
      });

      return this.upgradeJwtToken(jwtToken, convertJwtToApiKey);
    }
  }

  // Only the wallet can decrypt an instant JWT. Converting the result into an
  // API key is left to the caller, which has the authorized accounting gateway.
  public decryptInstantJwtToken(jwtData: string): Promise<string> {
    return this.tokenDecryptionService.decryptToken(jwtData);
  }
}
